import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../utils/api';

function WeddingCulturalReligious({ _embedded }) {
  const [formData, setFormData] = useState({
    ceremonyType: '',
    culturalNotes: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [detailsRes, partnersRes] = await Promise.all([
        api.get('/api/wedding-details'),
        api.get('/api/wedding-details/partners')
      ]);
      const details = detailsRes.data;
      const partners = partnersRes.data;
      // Migrate from per-partner if wedding-level is empty
      if (details?.cultural?.ceremonyType || details?.cultural?.culturalNotes) {
        setFormData({
          ceremonyType: details.cultural.ceremonyType || '',
          culturalNotes: details.cultural.culturalNotes || ''
        });
      } else if (partners?.partnerOne?.ceremonyType || partners?.partnerOne?.culturalNotes ||
                 partners?.partnerTwo?.ceremonyType || partners?.partnerTwo?.culturalNotes) {
        const p1 = partners.partnerOne || {};
        const p2 = partners.partnerTwo || {};
        setFormData({
          ceremonyType: p1.ceremonyType || p2.ceremonyType || '',
          culturalNotes: p1.culturalNotes || p2.culturalNotes || ''
        });
      }
    } catch (err) {
      setError('Failed to load cultural and religious information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      await api.post('/api/wedding-details', {
        cultural: {
          ceremonyType: formData.ceremonyType,
          culturalNotes: formData.culturalNotes
        }
      });
      setSuccess('Cultural and religious information saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save cultural and religious information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, px: 2 }}>
        Cultural & Religious Considerations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
        Share information about your ceremony type and any cultural traditions to help suppliers understand and respect your customs.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ceremony Type"
                value={formData.ceremonyType}
                onChange={(e) => setFormData(prev => ({ ...prev, ceremonyType: e.target.value }))}
                helperText="e.g., civil, Christian, Hindu, Jewish, Muslim, etc."
                placeholder="What type of ceremony are you planning?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Cultural Notes"
                value={formData.culturalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, culturalNotes: e.target.value }))}
                helperText="e.g., 'Tea ceremony before dinner', 'Traditional dance after ceremony'"
                placeholder="Any specific cultural traditions or customs?"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Information'}
        </Button>
      </Box>
    </Box>
  );
}

export default WeddingCulturalReligious;
